import {fpRequest} from '@/util/request';
import {buildMbApiUrl, fetchWithTimeout} from '@/util/request-utils';
import {FP_API_V2_URL} from '@/util/constants/api';
import {searchIO} from '@/actions/products/media';
import type {User} from '@/types/context';
import type {UserStats, UsageData} from '@/types/dashboard';

export async function getUserStats(user: User): Promise<UserStats> {
    // Note: This is Floorplanner API, not Modelbank - uses fpRequest
    const url = `${FP_API_V2_URL}/users/${user.id}/stats.json`;

    return await fpRequest<UserStats>(url, {authToken: user.auth_token});
}

export async function getUsageData(user: User): Promise<UsageData> {
    const url = buildMbApiUrl(
        '/usage/current.json',
        {auth_token: user.auth_token},
        {branding_id: user.branding.id}
    );

    const response = await fetchWithTimeout(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch usage data: ${response.status}`);
    }

    return response.json();
}

interface RenderItem {
    id: number;
    name: string;
    asset_id: string;
    asset_type: string;
    kind_type: string;
    sku?: string;
    fmt: string;
    width: number;
    height: number;
    state: string;
    created_at: string;
    updated_at: string;
    result?: {
        host: string;
        resolutions: Array<{width: number; height: number}>;
        views: Record<string, {path: string}>;
    };
}

export async function getRecentRenders() {
    const response = await searchIO({
        per_page: 10,
        order: 'desc',
    });

    const items = (response.items as RenderItem[])
        .filter(
            (item: RenderItem) =>
                item.state === 'success' &&
                item.result?.host &&
                Object.keys(item.result.views).length > 0
        )
        .map((item: RenderItem) => {
            const result = item.result!; // Already filtered for existence above
            const {host, resolutions, views} = result;
            const [hq] = resolutions;
            const lq = resolutions[resolutions.length - 1];
            const firstViewKey = Object.keys(views)[0];
            const firstViewPath = views[firstViewKey].path;
            const filename = `${item.id}_${firstViewKey}`;

            return {
                id: item.id.toString(),
                name: item.name,
                assetId: item.asset_id,
                assetType: item.asset_type,
                kind: item.kind_type,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should use fallback
                sku: item.sku || item.name || `Render ${item.id}`,
                fmt: item.fmt,
                width: item.width,
                height: item.height,
                state: item.state,
                image: `${host}/${firstViewPath}/${hq.width}x${hq.height}/${filename}.${item.fmt}`,
                thumbnail: `${host}/${firstViewPath}/${lq.width}x${lq.height}/${filename}.${item.fmt}`,
                createdAt: new Date(item.created_at),
                updatedAt: new Date(item.updated_at),
            };
        });

    return {items, total: response.total};
}
